import React, { useCallback, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { assign } from 'lodash';

import { t } from '~/helpers';
import { Spinner } from '~/components';
import { Input, Select, Form } from '~/components/Form';
import { storeProduto, updateProduto } from '~/store/modules/produto/actions';

import Transfer from '../TransferList';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.produto);

  const initials = create ? data : { ...find, ...data };

  const [id, setId] = useState(0);
  const [associados, setAssociados] = useState([]);

  const optionSN = useMemo(
    () => [
      { label: 'Sim', value: 'S' },
      { label: 'Não', value: 'N' },
    ],
    []
  );

  const optionType = useMemo(
    () => [
      { label: 'Lubrificante', value: 'L' },
      { label: 'Combustível', value: 'C' },
    ],
    []
  );

  const handleSubmit = useCallback(
    async (param) => {
      try {
        const schema = Yup.object().shape({
          prod_codigo: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10)),
          prod_descricao: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(30, t('s.geral.validacao.tamanhomaximo').replace('%s', 30)),
          prod_display: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(12, t('s.geral.validacao.tamanhomaximo').replace('%s', 12)),
          prod_codigo_aux: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(15, t('s.geral.validacao.tamanhomaximo').replace('%s', 15)),
          prod_codigo_nf: Yup.string()
            .nullable()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(50, t('s.geral.validacao.tamanhomaximo').replace('%s', 50)),
          prod_percentual_perdasobra: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(5, t('s.geral.validacao.tamanhomaximo').replace('%s', 5)),
          prod_dens_max_20: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(6, t('s.geral.validacao.tamanhomaximo').replace('%s', 6)),
          prod_dens_min_20: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(6, t('s.geral.validacao.tamanhomaximo').replace('%s', 6)),
          prod_var_max_dens_20: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(6, t('s.geral.validacao.tamanhomaximo').replace('%s', 6)),
          prod_tipo: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          prod_trata_agua: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
        });

        await schema.validate(param, {
          abortEarly: false,
        });

        const objSend = {
          ...initials,
          ...param,
          associados,
          idcliente: client,
        };

        if (create) dispatch(storeProduto(objSend, params));
        else dispatch(updateProduto(objSend, params));
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          reference.current.setErrors(validationErrors);
        }
      }
    },
    [create, initials, associados, client]
  );

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form
          ref={reference}
          initialData={assign(
            { prod_tipo: 'C', prod_trata_agua: 'N' },
            initials
          )}
          onSubmit={handleSubmit}
        >
          <Input
            name="prod_codigo"
            label="Código"
            type="number"
            getValue={(value) => setId(value)}
          />

          <Input name="prod_descricao" label="Descrição" />

          <Input name="prod_display" label="Display" />

          <Input name="prod_codigo_aux" label="Código Auxiliar" type="number" />

          <Input
            name="prod_codigo_nf"
            label="Código Nota Fiscal"
            type="number"
          />

          <Input
            name="prod_percentual_perdasobra"
            label="% de Perdas e Sobras"
            type="number"
          />

          <Input
            name="prod_dens_max_20"
            label="Densidade Máxima 20°"
            type="number"
          />

          <Input
            name="prod_dens_min_20"
            label="Densidade Miníma 20°"
            type="number"
          />

          <Input
            name="prod_var_max_dens_20"
            label="Variação Max. 20°"
            type="number"
          />

          <Select name="prod_tipo" label="Tipo" options={optionType} />

          <Select
            name="prod_trata_agua"
            label="Trata Água"
            options={optionSN}
          />

          <Transfer
            unique="prde_codigo"
            label="prde_nome"
            listRight={initials.associados || []}
            id={create ? -1 : id}
            onChangedItems={(values) => setAssociados(values)}
          />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
